import { HouseLine, X } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  AdvertisedOffer,
  advertisedOfferLabel,
  useAdvertisedOffer,
} from '../../AdvertisedOffer';
import {
  CatalogTest,
  IndexKind,
  KitCatalogProduct,
  LIVE_CATEGORIES,
  NEW_SPOTS,
  N_TESTS,
  TESTS,
  categoryOf,
  checkoutPath,
  displayName,
  eyebrowCase,
  indexKindPath,
  indexPath,
  kitCheckoutPath,
  kitDetailBySlug,
  kindOf,
  kitPath,
  learnMoreHref,
  nonBreakingTitle,
  oneLine,
  testPath,
} from '../../catalog';
import styled from '../../styled';
import { trackEverywhere } from '../../tracking';
import CardPhoto from '../CardPhoto';
import ChipRail from '../ChipRail';
import { revealInRail, useHydrated } from '../hooks';
import { routeOnClick, routeTo } from '../linkNav';
import { ChoicePillLink, Eyebrow, PillRow } from '../primitives';
import { useEverywhereApp } from '../Shell';
import { SpecimenTile } from '../Specimen';
import {
  BP,
  C,
  GUTTER,
  MAX_W,
  SPACE,
  TYPE_SIZE,
  display,
  mono,
  mq,
} from '../tokens';
import {
  PrepaintFilter,
  clearPrepaintFilter,
  countKey,
} from './prepaintFilter';

const Header = styled.div`
  max-width: ${MAX_W};
  margin: 0 auto;
  padding: 48px ${GUTTER} 16px;
  h1 {
    ${display}
    font-size: clamp(32px, 4vw, 44px);
    line-height: 1.2;
    margin: 0 0 8px;
  }
  p {
    margin: 0;
    font-size: 18px;
    color: ${C.ink500};
  }
`;

const NewTests = styled.section`
  max-width: ${MAX_W};
  margin: 0 auto;
  padding: ${SPACE.lg} ${GUTTER} ${SPACE.xl2};
  h2 {
    ${display}
    margin: 0;
    font-size: clamp(${TYPE_SIZE.lg}, 3vw, ${TYPE_SIZE.heading});
    line-height: 1.2;
  }
`;

const NewTestRail = styled.div`
  display: flex;
  gap: ${SPACE.sm};
  margin: ${SPACE.lg} calc(-1 * ${GUTTER}) -${SPACE.sm};
  padding: 0 ${GUTTER} ${SPACE.sm};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  ${mq(BP.mob)} {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${SPACE.md};
    margin: ${SPACE.lg} 0 0;
    padding: 0;
    overflow: visible;
  }
  ${mq(BP.xl)} {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
`;

const NewTestCard = styled.article`
  flex: 0 0 280px;
  min-width: 0;
  overflow: hidden;
  border: 1px solid ${C.canvas900};
  border-radius: 20px;
  background: #fff;
  display: flex;
  flex-direction: column;
  scroll-snap-align: start;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${C.evergreen500};
    outline-offset: 2px;
  }
`;

const NewTestOpen = styled.div`
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
`;

const NewTestPhoto = styled.span`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: ${C.canvas500};
  display: block;
  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
`;

const NewTestHeadline = styled.span`
  position: absolute;
  inset: 0;
  padding: ${SPACE.md};
  background: linear-gradient(to top, rgba(15, 15, 15, 0.7), transparent 65%);
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  .eyebrow {
    ${mono}
    font-size: ${TYPE_SIZE.label};
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .headline {
    ${display}
    margin-top: ${SPACE.xs1};
    font-size: ${TYPE_SIZE.bodyLg};
    line-height: 1.2;
  }
`;

const NewTestBody = styled.span`
  padding: ${SPACE.md} ${SPACE.md} 0;
  display: flex;
  align-items: center;
  gap: ${SPACE.sm};
  .name {
    font-size: ${TYPE_SIZE.body};
    font-weight: 500;
    line-height: 1.3;
  }
`;

const NewTestFooter = styled.div`
  margin-top: auto;
  padding: ${SPACE.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE.sm};
  .price {
    font-size: ${TYPE_SIZE.bodyLg};
    font-weight: 500;
  }
`;

const OrderButton = styled.button`
  min-width: 72px;
  min-height: 40px;
  padding: 0 ${SPACE.md};
  border: 0;
  border-radius: 9999px;
  background: ${C.forest900};
  color: #fff;
  font-size: ${TYPE_SIZE.small};
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background: ${C.evergreen500};
  }
`;

const Sticky = styled.div`
  position: sticky;
  top: 64px;
  z-index: 30;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(8px);
  box-shadow: inset 0 -1px 0 ${C.canvas900};
  > div {
    max-width: ${MAX_W};
    margin: 0 auto;
    padding: 12px ${GUTTER};
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

const Chips = styled(PillRow)`
  padding: 2px;
  mask-image: linear-gradient(
    90deg,
    rgba(0, 0, 0, calc(1 - var(--fl))),
    #000 48px,
    #000 calc(100% - 48px),
    rgba(0, 0, 0, calc(1 - var(--fr)))
  );
`;

/** Chip rail plus its scroll controls; the reset pill never scrolls away. */
const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ResetPill = styled(ChoicePillLink)`
  flex: none;
  gap: 6px;
  padding: 0 14px;
  font-size: ${TYPE_SIZE.small};
`;

const Letters = styled(PillRow)`
  gap: 2px;
  button {
    ${mono}
    flex: 1 1 0;
    min-width: 32px;
    height: 36px;
    border-radius: 8px;
    border: 0;
    background: none;
    font-size: 14px;
    cursor: pointer;
    &:hover {
      background: ${C.canvas500};
    }
    &:disabled {
      color: ${C.ink100};
      cursor: default;
      background: none;
    }
  }
`;

const Directory = styled.div`
  max-width: ${MAX_W};
  margin: 0 auto;
  padding: 8px ${GUTTER} 64px;
`;

const Group = styled.section`
  padding-top: 24px;
  && h2 {
    ${display}
    font-size: 28px;
    color: ${C.ink300};
    padding: 8px 0;
    margin: 0;
  }
`;

const Row = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  grid-template-areas: 'art main order';
  column-gap: 12px;
  align-items: center;
  padding: 14px 8px;
  min-height: 76px;
  box-shadow: inset 0 -1px 0 ${C.canvas500};
  transition: background 250ms ease;
  cursor: pointer;
  &[data-row-kind='kit'] {
    grid-template-areas:
      'art main order'
      'art badge order';
    row-gap: 8px;
  }
  &:hover {
    background: ${C.canvas300};
  }
  .art {
    grid-area: art;
    flex: none;
  }
  .main {
    grid-area: main;
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    color: inherit;
    text-decoration: none;
  }
  .name {
    font-size: 18px;
    font-weight: 500;
    line-height: 1.35;
  }
  .aka {
    font-size: 14px;
    line-height: 1.45;
    color: ${C.ink500};
  }
  .price-m {
    margin-top: 6px;
    font-size: 14px;
    font-weight: 500;
    ${mq(BP.lg)} {
      display: none;
    }
  }
  .cat {
    ${mono}
    display: none;
    width: 180px;
    flex: none;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${C.ink500};
    border: 0;
    background: none;
    text-align: left;
    cursor: inherit;
    padding: 0;
    text-decoration: none;
    ${mq(BP.xl2)} {
      display: block;
    }
  }
  /* Wide enough for a struck price, the promo price, and "with code X"
     on two compact lines, and the same width on every row so Order stays
     aligned. */
  .price {
    display: none;
    width: 148px;
    flex: none;
    text-align: right;
    font-size: 18px;
    font-weight: 500;
    ${mq(BP.lg)} {
      display: block;
    }
  }
  .order {
    grid-area: order;
    position: relative;
    z-index: 2;
    height: 40px;
    padding: 0 18px;
    border-radius: 9999px;
    border: 0;
    background: ${C.forest900};
    color: #fff;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    flex: none;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    &:hover {
      background: ${C.evergreen900};
    }
  }
  .at-home {
    grid-area: badge;
    justify-self: start;
  }
  ${mq(BP.lg)} {
    display: flex;
    gap: 16px;
    &[data-row-kind='kit'] .name {
      flex: 0 0 auto;
    }
    &[data-row-kind='kit'] .aka {
      flex: 1 1 auto;
      min-width: 0;
    }
  }
`;

const RowHit = styled.a`
  position: absolute;
  inset: 0;
  z-index: 1;
  color: inherit;
  text-decoration: none;
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${C.forest900};
  }
`;

const KitTile = styled.span`
  width: 48px;
  height: 48px;
  overflow: hidden;
  border-radius: 12px;
  background: ${C.canvas300};
  color: ${C.evergreen500};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  img {
    width: 80%;
    height: 80%;
    display: block;
    object-fit: contain;
  }
`;

const EmptyMsg = styled.div`
  padding: 48px 0;
  text-align: center;
  font-size: 18px;
  color: ${C.ink500};
`;

const AtHomeBadge = styled.span`
  min-height: 32px;
  padding: 0 12px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: ${C.canvas300};
  color: ${C.ink500};
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
`;

const ORDER = ['#'].concat('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''));
const SHOW_NEW_TESTS = false;
const NAME_COLLATOR = new Intl.Collator('en', {
  numeric: true,
  sensitivity: 'base',
});

const rowMeta = (test: CatalogTest): string => oneLine(test);

type DirectoryItem =
  | { kind: 'lab'; test: CatalogTest }
  | { kind: 'kit'; kit: KitCatalogProduct };

const itemName = (item: DirectoryItem): string =>
  item.kind === 'kit' ? item.kit.name : item.test.name;

/** The catalog rows a filter selects. Shared by the page and its pre-paint. */
function selectItems(
  category: string | null,
  kind: IndexKind | null,
  kits: KitCatalogProduct[],
  needle: string,
): DirectoryItem[] {
  let items: DirectoryItem[];
  if (kind === 'kit') {
    items = kits.map((kit) => ({ kind: 'kit' as const, kit }));
  } else {
    const tests =
      kind === 'panel'
        ? TESTS.filter((test) => kindOf(test) === 'panel')
        : category
        ? TESTS.filter((test) => test.category === category)
        : TESTS;
    items = tests.map((test) => ({ kind: 'lab' as const, test }));
    if (!kind) {
      const categoryKits = category
        ? kits.filter((kit) => kitDetailBySlug(kit.slug)?.category === category)
        : kits;
      items.push(...categoryKits.map((kit) => ({ kind: 'kit' as const, kit })));
    }
  }
  if (!needle) return items;
  return items.filter((item) => {
    const values =
      item.kind === 'kit'
        ? [item.kit.name, item.kit.description, item.kit.slug]
        : [item.test.name, item.test.also_known_as, item.test.url_slug];
    return values.filter(Boolean).join(' ').toLowerCase().includes(needle);
  });
}

/** The line under the title: how many rows, and what they have in common. */
function catalogSummary(
  items: DirectoryItem[],
  kind: IndexKind | null,
  category: string | null = null,
): string {
  const count =
    kind === 'kit'
      ? `${items.length} tests · at home`
      : !kind && !category
      ? `${items.length} tests`
      : `${items.length} of ${N_TESTS} tests`;
  const broad = kind === 'panel' ? ' · broad checks' : '';
  const reviewed =
    kind !== 'kit'
      ? " · every one reviewed by a licensed clinician before it's ordered."
      : '';
  return `${count}${broad}${reviewed}`;
}

function DirectoryRow({
  item,
  showCategory,
}: {
  item: DirectoryItem;
  showCategory: boolean;
}): JSX.Element {
  const app = useEverywhereApp();
  const slug = item.kind === 'kit' ? item.kit.slug : item.test.slug;
  const price = item.kind === 'kit' ? item.kit.price : item.test.price;
  const offer = useAdvertisedOffer(slug, price);
  const priceLabel = advertisedOfferLabel(price, offer);

  if (item.kind === 'kit') {
    const kit = item.kit;
    const href = kitPath(kit.slug);
    return (
      <Row data-directory-item={kit.name} data-row-kind="kit">
        <RowHit
          href={href}
          aria-label={`${kit.name}, ${priceLabel}`}
          onClick={(event) => routeTo(event, href)}
        />
        <KitTile
          className="art"
          aria-hidden="true"
          data-directory-illustration="kit"
        >
          {kit.imageUrl ? (
            <img src={kit.imageUrl} alt="" loading="lazy" />
          ) : (
            <HouseLine size={22} />
          )}
        </KitTile>
        <div className="main">
          <span className="name">{nonBreakingTitle(kit.name)}</span>
          <span className="aka">{kit.description}</span>
          <span className="price-m">
            <AdvertisedOffer slug={kit.slug} price={kit.price} />
          </span>
        </div>
        <AtHomeBadge className="at-home">
          <HouseLine size={16} aria-hidden />
          At home
        </AtHomeBadge>
        <span className="price">
          <AdvertisedOffer slug={kit.slug} price={kit.price} align="end" />
        </span>
        <a
          className="order"
          href={kitCheckoutPath(kit.slug)}
          aria-label={`Order ${kit.name}`}
          onClick={(event) => event.stopPropagation()}
        >
          Order
        </a>
      </Row>
    );
  }

  const test = item.test;
  const href = testPath(test);
  const learnMore = learnMoreHref(test);
  return (
    <Row
      data-directory-item={test.name}
      data-row-kind="lab"
      data-row-cat={test.category}
      data-row-panel={kindOf(test) === 'panel' ? '1' : '0'}
    >
      <RowHit
        href={href}
        aria-label={`${displayName(test)}, ${priceLabel}`}
        onClick={(event) => routeTo(event, href)}
      />
      <span className="art">
        <SpecimenTile test={test} size={48} radius={12} art={34} />
      </span>
      <div className="main">
        <span className="name">{nonBreakingTitle(test.name)}</span>
        <span className="aka">{rowMeta(test)}</span>
        <span className="price-m">
          <AdvertisedOffer slug={test.slug} price={test.price} />
        </span>
      </div>
      {showCategory ? (
        <span className="cat">{categoryOf(test).chip}</span>
      ) : null}
      <span className="price">
        <AdvertisedOffer slug={test.slug} price={test.price} align="end" />
      </span>
      <a
        className="order"
        href={learnMore ?? checkoutPath(test)}
        aria-label={`${learnMore ? 'Learn more about' : 'Order'} ${displayName(
          test,
        )}`}
        onClick={(event) => {
          event.stopPropagation();
          if (learnMore) return;
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
            return;
          event.preventDefault();
          app.orderTest(test, 'index');
        }}
      >
        {learnMore ? 'Learn more' : 'Order'}
      </a>
    </Row>
  );
}

function NewTestsSection(): JSX.Element | null {
  const app = useEverywhereApp();
  if (NEW_SPOTS.length === 0) return null;

  return (
    <NewTests aria-labelledby="ew-new-tests-title" data-new-tests>
      <h2 id="ew-new-tests-title">New tests</h2>
      <NewTestRail>
        {NEW_SPOTS.map(({ key, row: test, image, headline }) => (
          <NewTestCard
            key={key}
            data-new-test={test.name}
            role="link"
            tabIndex={0}
            onClick={() => app.openTest(test, 'index_new_tests')}
            onKeyDown={(event) => {
              if (event.target === event.currentTarget && event.key === 'Enter')
                app.openTest(test, 'index_new_tests');
            }}
          >
            <NewTestOpen>
              <NewTestPhoto>
                <CardPhoto image={image} />
                <NewTestHeadline>
                  <span className="eyebrow">
                    {eyebrowCase(categoryOf(test).chip)}
                  </span>
                  <span className="headline">{headline}</span>
                </NewTestHeadline>
              </NewTestPhoto>
              <NewTestBody>
                <SpecimenTile test={test} size={48} radius={12} art={34} />
                <span className="name">
                  {nonBreakingTitle(displayName(test))}
                </span>
              </NewTestBody>
            </NewTestOpen>
            <NewTestFooter>
              <span className="price">
                <AdvertisedOffer slug={test.slug} price={test.price} />
              </span>
              <OrderButton
                type="button"
                aria-label={`${
                  learnMoreHref(test) ? 'Learn more about' : 'Order'
                } ${displayName(test)}`}
                onClick={(event) => {
                  event.stopPropagation();
                  const learnMore = learnMoreHref(test);
                  if (learnMore) {
                    window.location.href = learnMore;
                    return;
                  }
                  app.orderTest(test, 'index_new_tests');
                }}
              >
                {learnMoreHref(test) ? 'Learn more' : 'Order'}
              </OrderButton>
            </NewTestFooter>
          </NewTestCard>
        ))}
      </NewTestRail>
    </NewTests>
  );
}

export default function IndexPage({
  category,
  kind = null,
  kits = [],
  query = '',
}: {
  category: string | null;
  kind?: IndexKind | null;
  kits?: KitCatalogProduct[];
  query?: string;
}): JSX.Element {
  const app = useEverywhereApp();
  const chipRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const centred = useRef(false);
  const userScrolled = useRef(false);

  // The served HTML knows nothing about `?cat=` — it is the same document for
  // every filter — so the first pass renders the whole catalog and lets the
  // pre-paint rules narrow it. React filters for real from hydration on.
  const hydrated = useHydrated();
  const activeCategory = hydrated ? category : null;
  const activeKind = hydrated ? kind : null;
  const needle = (hydrated ? query : '').trim().toLowerCase();

  const filtered = useMemo(
    () => selectItems(activeCategory, activeKind, kits, needle),
    [activeCategory, activeKind, kits, needle],
  );
  const ordered = useMemo(
    () =>
      [...filtered].sort((a, b) =>
        NAME_COLLATOR.compare(itemName(a), itemName(b)),
      ),
    [filtered],
  );
  const byLetter = useMemo(() => {
    const itemsByLetter: Record<string, DirectoryItem[]> = {};
    for (const item of ordered) {
      const name = itemName(item);
      const ch = /[0-9]/.test(name[0]) ? '#' : name[0].toUpperCase();
      (itemsByLetter[ch] = itemsByLetter[ch] || []).push(item);
    }
    return itemsByLetter;
  }, [ordered]);
  // Every summary the pre-paint rules may need to show, since the script that
  // picks one runs before React knows which filter is in the URL.
  const summaries = hydrated
    ? null
    : [null, ...LIVE_CATEGORIES.map((c) => c.key)]
        .map((key) => ({ key, kind: null as IndexKind | null }))
        .concat([
          { key: null, kind: 'panel' },
          { key: null, kind: 'kit' },
        ])
        .map(({ key, kind: variantKind }) => ({
          slot: countKey(key, variantKind),
          text: catalogSummary(
            selectItems(key, variantKind, kits, ''),
            variantKind,
            key,
          ),
        }));

  const showCat = !activeCategory && !activeKind;

  useEffect(() => {
    if (hydrated) clearPrepaintFilter();
  }, [hydrated]);

  // Bring the selected chip fully into view, centred when the rail has room
  // and clamped at either end so the last chip is never cut off by the edge.
  const revealChip = useCallback(
    (behavior: ScrollBehavior) => {
      const el = chipRefs.current[activeKind || activeCategory || 'all'];
      const rail = chipRef.current;
      if (el && rail) revealInRail(rail, el, behavior);
    },
    [activeCategory, activeKind, chipRef],
  );

  // The first pass lands on the chip rather than gliding there, so a deep
  // link does not animate the rail while the page settles. Web fonts and the
  // arrow buttons change the rail's geometry after that first pass, so the
  // reveal repeats until the visitor starts scrolling the rail themselves.
  useEffect(() => {
    revealChip(centred.current ? 'smooth' : 'auto');
    centred.current = true;
    userScrolled.current = false;
    const rail = chipRef.current;
    if (!rail) return undefined;
    let cancelled = false;
    const settle = () => {
      if (!cancelled && !userScrolled.current) revealChip('auto');
    };
    void document.fonts?.ready.then(settle);
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(settle) : null;
    ro?.observe(rail);
    const markScrolled = () => {
      userScrolled.current = true;
    };
    rail.addEventListener('wheel', markScrolled, { passive: true });
    rail.addEventListener('touchstart', markScrolled, { passive: true });
    rail.addEventListener('pointerdown', markScrolled, { passive: true });
    return () => {
      cancelled = true;
      ro?.disconnect();
      rail.removeEventListener('wheel', markScrolled);
      rail.removeEventListener('touchstart', markScrolled);
      rail.removeEventListener('pointerdown', markScrolled);
    };
  }, [revealChip, chipRef]);

  const jump = (ch: string) => {
    const el = document.getElementById(`az-${ch === '#' ? 'num' : ch}`);
    if (el)
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 170,
        behavior: 'smooth',
      });
  };

  return (
    <>
      {summaries ? <PrepaintFilter /> : null}
      <Header>
        <Eyebrow>The full catalog</Eyebrow>
        <h1>All tests, A to Z</h1>
        <p data-catalog-summary>
          {summaries
            ? summaries.map(({ slot, text }) => (
                <span key={slot} data-count-for={slot}>
                  {text}
                </span>
              ))
            : catalogSummary(filtered, activeKind, activeCategory)}
        </p>
      </Header>
      {SHOW_NEW_TESTS && !activeCategory && !activeKind && !needle ? (
        <NewTestsSection />
      ) : null}
      <Sticky>
        <div>
          <FilterBar>
            <ChipRail
              ref={chipRef}
              track={Chips}
              label="categories"
              role="tablist"
              tabIndex={0}
              data-catalog-chips
            >
              <ChoicePillLink
                ref={(el) => {
                  chipRefs.current.all = el;
                }}
                href={indexPath()}
                data-chip="all"
                role="tab"
                aria-selected={!activeCategory && !activeKind}
                $on={!activeCategory && !activeKind}
                $h={44}
                $stroke
                onClick={(event: React.MouseEvent) =>
                  routeOnClick(event, () => {
                    trackEverywhere.categoryOpened('all');
                    app.goIndex();
                  })
                }
              >
                All
              </ChoicePillLink>
              <ChoicePillLink
                ref={(el) => {
                  chipRefs.current.panel = el;
                }}
                href={indexKindPath('panel')}
                data-chip="panel"
                role="tab"
                aria-selected={activeKind === 'panel'}
                $on={activeKind === 'panel'}
                $h={44}
                $stroke
                onClick={(event: React.MouseEvent) =>
                  routeOnClick(event, () =>
                    app.goIndex(undefined, { kind: 'panel' }),
                  )
                }
              >
                Broad checks
              </ChoicePillLink>
              <ChoicePillLink
                ref={(el) => {
                  chipRefs.current.kit = el;
                }}
                href={indexKindPath('kit')}
                data-chip="kit"
                role="tab"
                aria-selected={activeKind === 'kit'}
                $on={activeKind === 'kit'}
                $h={44}
                $stroke
                onClick={(event: React.MouseEvent) =>
                  routeOnClick(event, () =>
                    app.goIndex(undefined, { kind: 'kit' }),
                  )
                }
              >
                At home
              </ChoicePillLink>
              {LIVE_CATEGORIES.map((c) => (
                <ChoicePillLink
                  key={c.key}
                  ref={(el) => {
                    chipRefs.current[c.key] = el;
                  }}
                  href={indexPath(c.key)}
                  data-chip={c.key}
                  role="tab"
                  aria-selected={activeCategory === c.key}
                  $on={activeCategory === c.key}
                  $h={44}
                  $stroke
                  onClick={(event: React.MouseEvent) =>
                    routeOnClick(event, () => app.goIndex(c.key))
                  }
                >
                  {c.chip}
                </ChoicePillLink>
              ))}
            </ChipRail>
            {activeCategory || activeKind || !hydrated ? (
              <ResetPill
                href={indexPath()}
                data-reset-pill
                $on={false}
                $h={36}
                $stroke
                onClick={(event: React.MouseEvent) =>
                  routeOnClick(event, () => {
                    trackEverywhere.categoryOpened('all');
                    app.goIndex();
                  })
                }
              >
                <X size={14} weight="bold" />
                Clear
              </ResetPill>
            ) : null}
          </FilterBar>
          <ChipRail track={Letters} label="letters" aria-label="Jump to letter">
            {ORDER.map((ch) => (
              <button
                key={ch}
                type="button"
                disabled={!byLetter[ch]}
                onClick={() => jump(ch)}
              >
                {ch}
              </button>
            ))}
          </ChipRail>
        </div>
      </Sticky>
      <Directory data-directory>
        {filtered.length === 0 ? (
          <EmptyMsg>
            No catalog match{query.trim() ? ` for “${query.trim()}”` : ''}.
          </EmptyMsg>
        ) : null}
        {ORDER.filter((ch) => byLetter[ch]).map((ch) => (
          <Group
            key={ch}
            id={`az-${ch === '#' ? 'num' : ch}`}
            aria-label={ch}
            data-az-group={ch}
          >
            <h2>{ch}</h2>
            {byLetter[ch].map((item) => (
              <DirectoryRow
                key={
                  item.kind === 'kit'
                    ? `kit-${item.kit.slug}`
                    : `lab-${item.test.slug}`
                }
                item={item}
                showCategory={showCat}
              />
            ))}
          </Group>
        ))}
      </Directory>
    </>
  );
}
